import { Component, EventEmitter, OnInit, Output} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, Validators,AsyncValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { CmElement, CmHelp, CmSection, ContentType } from '../shared/model/cm/managed-content.model';
import { DeactivateGuard } from '../deactivate.guard';
import { of } from 'rxjs';
import { UserSessionService } from '../shared/service/user-session.service';
import { AppInsightsApiService} from "../shared/service/app-insights-api.service";
import { PagetitleService } from '../shared/service/pagetitle.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { ViewportScroller } from '@angular/common';
import { FormUtils } from '../shared/helper/form-utils';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { COMPANY_ID, GlobalConstants } from '../shared/global-constants';
import { EConsent } from '../shared/model/ui/eConsent';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { MhsService } from '../shared/service/mhs.service';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';

@Component({
  selector: 'app-esign-disclosure',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './esign-disclosure.component.html',
  styleUrls: ['./esign-disclosure.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EsignDisclosureComponent implements OnInit, DeactivateGuard {
  @Output() cancelEmitter = new EventEmitter<void>();
  public readonly pageIllustrationUrl = 'assets/Illustration-Footer-Base-Mountains.svg';

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  public stateOfResidence: string;
  public companyId: string;

  esignDiscContent: CmSection;
  eSignDiscContentData: any[]= [];
  submitted: boolean = false;
  agreeButton: string = "Next"
  eConsent: EConsent;
  // FormControl metadata from drupal for consent field
  consentElement: CmElement;
  emailElement: CmElement;
  
  public emailName: string = "email";
  // Form definition and alias
  consentForm = new FormGroup({
    consent: new FormControl(false, [Validators.requiredTrue])
  })
  
  public printButtonProps = {
    icon: 'print',
    keyId: 'print-button',
    size: 'large',
    text: 'Print',
    type: 'button',
    variant: 'text'
  }

  // GA4 Custom events
  public submitDisclosure = {
    action: "button_click",
    category: "esign-disclosure",
    name: "submit",
  }

  public printDisclosure = {
    action: "button_click",
    category: "esign-disclosure",
    name: "print",
  }

  public cancelDisclosure = {
    action: "button_click",
    category: "esign-disclosure",
    name: "cancel",
  }

  constructor(private router: Router,
              private cmService: ManagedContentService,
              private fb: UntypedFormBuilder,
              private userSessionService: UserSessionService,
              private appInsightsApiService: AppInsightsApiService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private pagetitleService: PagetitleService,
              private authGuardService: AuthGuardService,
              private scroller: ViewportScroller,
              private modalService: ModalService,
              private windowService: WindowRef,
              private appInsightsLoggerService: AppInsightsLoggerService,
              public formControlUtilitiesService: FormControlUtilitiesService,
              private mhsService: MhsService) { }

  private log(content: string) {
    this.appInsightsLoggerService.log('info', 'Info log from applicant-info.component', content);
  }

  get email() { 
    if(this.consentForm && this.consentForm.contains("email")){
      return this.consentForm.get("email");
    }
    return null; 
  }
  get consent() { 
    if(this.consentForm && this.consentForm.contains("consent")){
      return this.consentForm.get("consent");
    }
    return null;
  }
  
  ngOnInit() { 
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }
    this.stateOfResidence = this.userSessionService.data.state;
    this.companyId = this.userSessionService.data.companyId;
    
    if(this.companyId === COMPANY_ID.SI){
      this.consentForm = this.fb.group({
        email: ['', [ Validators.pattern(GlobalConstants.EMAIL_PATTERN), Validators.maxLength(255), Validators.required]],
        consent: [false, [Validators.requiredTrue]],
      });
      this.consentElement = this.cmService.getContentById(ContentType.ELEMENT, "consent");
    }else if(this.companyId === COMPANY_ID.NM){
      this.consentForm = this.fb.group({
        email: ['', [ Validators.pattern(GlobalConstants.EMAIL_PATTERN), Validators.maxLength(255), Validators.required]],
        consent: [false, [Validators.requiredTrue]],
      });
      this.consentElement = this.cmService.getContentById(ContentType.ELEMENT, "consent-nm");
    }
    
    const emailControl = this.consentForm.get("email");
      if (emailControl) {
        emailControl.setAsyncValidators([this.validateEmailAddress()]);
        emailControl.updateValueAndValidity();
      }
    this.emailElement = this.cmService.getContentById(ContentType.ELEMENT, 'email');
    this.loadEsignDiscContent();
    this.splitUpContent();
    this.pagetitleService.setPageTitle('E-Signature Disclosure');
    this.appInsightsApiService.endNavigationEvent(this.router.url);

    this.windowService.nativeDocument
      .querySelector('body')
      .addEventListener('click', (event: Event) => this.onClick(event));
    
  }

  loadEsignDiscContent(){
    if(this.stateOfResidence == "CA" && this.userSessionService.data.companyId === COMPANY_ID.SI){
        this.esignDiscContent = this.cmService.getContentById(ContentType.SECTION, "ca-esig-disclosure");
    }

    if(this.stateOfResidence == "MA"){
      if (this.userSessionService.data.dataTheme != 'nwm'){
        //NOTE- this content is not in drupal.  Drupal breaks the HTML in the ordered list.
        this.esignDiscContent = this.cmService.getContentById(ContentType.SECTION, "ma-esig-disclosure-not-in-drupal");
      } else {
        this.esignDiscContent = this.cmService.getContentById(ContentType.SECTION, "nm-ma-esig-disclosure-not-in-drupal");
      }
    }
  }

  splitUpContent(){
    if(this.stateOfResidence == "CA"){
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.esignDiscContent.body;
      const pElements = tempDiv.querySelectorAll('p');
      pElements.forEach(p => {
        const className = p.getAttribute('class').replace(/["\\]/g, '');
        const innerHtml = p.innerHTML.trim();
        if(className) {
          this.eSignDiscContentData[className] = innerHtml;
        }
      });
    } else if(this.stateOfResidence == "MA"){
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.esignDiscContent.body, "application/xml");
      const pElements = xmlDoc.getElementsByTagName("p");

      for (let i = 0; i < pElements.length; i++) {
        const className = pElements[i].getAttribute('class').replace(/["\\]/g, '');
        const innerHtml = pElements[i].innerHTML;
        if(className) {
          this.eSignDiscContentData[className] = innerHtml;
        }
      }
    }
  }

  onPrint() {
    this.googleAnalyticsService.sendCustomEvent(this.printDisclosure);
    var headerContents = document.getElementById("header").innerHTML;
    var divContents = document.getElementById("printable").innerHTML;
    var printWindow = window.open('', '', 'height=750, width=750');
    printWindow.document.write('<html>');
    printWindow.document.write('<body >');
    printWindow.document.write(headerContents);
    printWindow.document.write(divContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  }

  onSubmit() {
    this.googleAnalyticsService.sendCustomEvent(this.submitDisclosure);
    this.consentForm.markAllAsTouched();
    this.consentForm.updateValueAndValidity();
    this.formControlUtilitiesService.setCheckboxFormValid(this.consentForm);
    if(this.consentForm.valid){

      this.setEConsent();

      this.submitted = true;
      this.router.navigate(['/applicant-type']);
      const navigation = this.router.getCurrentNavigation();
      if (navigation) {
        this.appInsightsApiService.startNavigationEvent(navigation.extractedUrl.toString());
      }
    }
  }

  setEConsent(){
    const today: string = new Date().toISOString().split('T')[0];
    this.eConsent = new EConsent();
    this.eConsent.body = btoa(encodeURIComponent(this.eSignDiscContentData['body']).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))));
    this.eConsent.signatureDate = today;
    if(this.consentForm.contains('email')){
      this.eConsent.email = this.consentForm.get('email').value;
    }
    this.eConsent.acknowledgementText = this.eSignDiscContentData['acknowledgementText'];
    this.eConsent.eSignFormNumber = this.eSignDiscContentData['eSignFormNumber'];
    this.eConsent.filingLabel = this.eSignDiscContentData['filingLabel'];
    this.eConsent.filingDate = this.eSignDiscContentData['filingDate'];
    this.eConsent.headerLine1 = this.eSignDiscContentData['headerLine1'];
    this.eConsent.headerLine2 = this.eSignDiscContentData['headerLine2'];
    this.userSessionService.data.mhsApplication.econsent = this.eConsent;
  }

  onCancel() {
    this.googleAnalyticsService.sendCustomEvent(this.cancelDisclosure);
    this.cancelEmitter.emit();
    this.canDeactivate();
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from ApplicantInfo.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-cancel-modal'
    );

    if (modal && event.target === modal) {
      this.onCancelModal('prism-ui-modal-cancel-modal');
    }
  }

  canDeactivate(){
    if (!this.submitted && !this.userSessionService.data.sessionEnded) {
      this.submitted = false;
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal     
    } else {
      return of(true);
    }
  }
  validateEmailAddress(): AsyncValidatorFn {
  return (email: AbstractControl): Promise<ValidationErrors | null> => {

    return new Promise((resolve) => {

      if (!email.value) {
        resolve(null);
        return;
      }

      if (!this.mhsService) {
        resolve(null);
        return;
      }

      this.mhsService.isValidEmailAddress(email.value).subscribe(
        (res) => {
          resolve(res ? null : { invalidEmail: true });
        },
        (err) => {
          this.log(
            'Invalid email address entered: ' +
            email.value +
            ' Error: ' +
            JSON.stringify(err)
          );
          resolve({ invalidEmail: true });
        }
      );

    });
  };
}

}